// test-mapping-results.tsx
import { Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, Stack, Typography } from '@mui/material';
import PaiListItem from 'features/ui/base-components/pai-list-item';
import PaiButton from 'features/ui/base-components/pai-button';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface TestMappingResultsProps {
  open: boolean;
  onClose: () => void;
  testResult: any;
}

const TestMappingResults: React.FC<TestMappingResultsProps> = ({ open, onClose, testResult }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  if (!testResult || !testResult.sessionsData || !testResult.sessionsData.result) {
    return null;
  }

  const results = testResult.sessionsData.result;

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'md'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('testMappingResultsTitle')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          {results.map((company: any, index: number) => {
            const visitedUrls = company.sessions
              ? company.sessions.reduce((acc: any[], session: any) => acc.concat(session.user_data || []), [])
              : [];

            return (
              <Card key={index} className={classes.card} variant={'outlined'}>
                <CardContent className={classes.cardContent}>
                  <Stack gap={2}>
                    <Typography className={classes.cardTitle}>{company.company_name}</Typography>

                    {(company.email_address || company.phone_number || company.address || company.postal_code || company.city || company.country) && (
                      <>
                        <Divider />
                        <Stack gap={1}>
                          <Typography className={classes.cardSubTitle}>{t('contactInformation')}</Typography>
                          <div>
                            {company.email_address && (
                              <Typography className={classes.cardText}>
                                {t('email')}: {company.email_address}
                              </Typography>
                            )}
                            {company.phone_number && (
                              <Typography className={classes.cardText}>
                                {t('phone')}: {company.phone_number}
                              </Typography>
                            )}
                            {(company.address || company.postal_code || company.city || company.country) && (
                              <Typography className={classes.cardText}>
                                {t('address')}: {[company.address, company.postal_code, company.city, company.country].filter(Boolean).join(', ')}
                              </Typography>
                            )}
                          </div>
                        </Stack>
                      </>
                    )}

                    <Divider />
                    <Stack gap={1}>
                      <Typography className={classes.cardSubTitle}>{t('visitedWebsitesReferrer')}</Typography>
                      {visitedUrls.length > 0 ? (
                        <List className={classes.list}>
                          {visitedUrls.map((ud: any, idx: number) => (
                            <PaiListItem key={idx} size={'medium'} text={ud.referrer || ud.document_location} subText={ud.document_title} />
                          ))}
                        </List>
                      ) : (
                        <Typography className={classes.cardText}>{t('noVisitedWebsites')}</Typography>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiButton text={t('close')} size={'small'} border={'rounded'} action={onClose} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  card: {
    borderRadius: '8px',
  },
  cardContent: {
    padding: '16px !important',
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  cardSubTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
  },
  cardText: {
    fontSize: theme.typography.pxToRem(14),
  },
  list: {
    padding: '0 !important',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default TestMappingResults;
